import { getModeHeights, TABLE_MODES } from '../constants/agGridStyles';

export const AG_GRID_SIZING = {
  LEGACY: 'legacy',
  CONTENT: 'content',
};

export const AG_GRID_FIT_MODES = {
  COLUMNS: 'columns',
  CONTAINER: 'container',
};

export const AG_GRID_DEFAULTS = {
  minWidth: 500,
  maxWidth: Infinity,
  widthBuffer: 8,
  columnBorderReserve: 1,
  scrollbarReserve: 16,
  minHeight: 96,
  maxHeight: 800,
  heightBuffer: 10,
};

const toFiniteNumber = (value, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const resolveColumnWidth = (colDef) => {
  if (!colDef || colDef.hide) return 0;
  return toFiniteNumber(colDef.width ?? colDef.minWidth, 100);
};

const resolveColumnTreeWidth = (colDef) => {
  if (!colDef || colDef.hide) return 0;
  if (Array.isArray(colDef.children) && colDef.children.length) {
    return colDef.children.reduce((sum, childDef) => {
      if (!childDef || childDef.hide) return sum;
      return sum + resolveColumnTreeWidth(childDef);
    }, 0);
  }
  return resolveColumnWidth(colDef);
};

const countVisibleLeafColumns = (colDef) => {
  if (!colDef || colDef.hide) return 0;
  if (Array.isArray(colDef.children) && colDef.children.length) {
    return colDef.children.reduce((sum, childDef) => sum + countVisibleLeafColumns(childDef), 0);
  }
  return 1;
};

export const hasAgGridColumnGroups = (columnDefs = []) => (
  Array.isArray(columnDefs)
  && columnDefs.some((colDef) => Array.isArray(colDef?.children) && colDef.children.length > 0)
);

export const calculateAgGridTableWidth = (columnDefs = [], options = {}) => {
  const {
    fitMode = AG_GRID_FIT_MODES.COLUMNS,
    minWidth = AG_GRID_DEFAULTS.minWidth,
    maxWidth = AG_GRID_DEFAULTS.maxWidth,
    widthBuffer = AG_GRID_DEFAULTS.widthBuffer,
    columnBorderReserve = AG_GRID_DEFAULTS.columnBorderReserve,
    reserveScrollbar = true,
    scrollbarReserve = AG_GRID_DEFAULTS.scrollbarReserve,
  } = options;

  if (fitMode === AG_GRID_FIT_MODES.CONTAINER) {
    return '100%';
  }

  const columnsWidth = Array.isArray(columnDefs)
    ? columnDefs.reduce((sum, colDef) => {
        if (!colDef || colDef.hide) return sum;
        return sum + resolveColumnTreeWidth(colDef);
      }, 0)
    : 0;
  const visibleColumnCount = Array.isArray(columnDefs)
    ? columnDefs.reduce((sum, colDef) => sum + countVisibleLeafColumns(colDef), 0)
    : 0;
  const borderReserve = visibleColumnCount * toFiniteNumber(columnBorderReserve, 0);

  const contentWidth = Math.max(
    minWidth,
    columnsWidth + borderReserve + widthBuffer + (reserveScrollbar ? scrollbarReserve : 0)
  );
  const resolvedMaxWidth = toFiniteNumber(maxWidth, Infinity);

  return Math.min(contentWidth, resolvedMaxWidth);
};

export const calculateAgGridTableHeight = (rowCount = 0, options = {}) => {
  const {
    mode = TABLE_MODES.COMPSHEET,
    includePinnedRows = false,
    pinnedRowCount,
    headerHeight,
    groupHeaderHeight,
    rowHeight,
    rowData,
    getRowHeight,
    pinnedRowHeight,
    hasColumnGroups = false,
    minHeight = AG_GRID_DEFAULTS.minHeight,
    maxHeight = AG_GRID_DEFAULTS.maxHeight,
    heightBuffer = AG_GRID_DEFAULTS.heightBuffer,
  } = options;

  const heights = getModeHeights(mode);
  const dataRows = Math.max(0, toFiniteNumber(rowCount, 0));
  const statsRows = Number.isFinite(Number(pinnedRowCount))
    ? Number(pinnedRowCount)
    : includePinnedRows && mode === TABLE_MODES.COMPSHEET
      ? 2
      : 0;
  const resolvedRowHeight = toFiniteNumber(rowHeight, heights.rowHeight);
  const dataRowsHeight = Array.isArray(rowData) && typeof getRowHeight === 'function'
    ? rowData.reduce((sum, row, rowIndex) => (
      sum + toFiniteNumber(getRowHeight({ data: row, rowIndex }), resolvedRowHeight)
    ), 0)
    : dataRows * resolvedRowHeight;
  const resolvedPinnedRowHeight = toFiniteNumber(
    pinnedRowHeight,
    heights.pinnedRowHeight || resolvedRowHeight
  );
  const resolvedHeaderHeight =
    toFiniteNumber(headerHeight, heights.headerHeight)
    + (hasColumnGroups ? toFiniteNumber(groupHeaderHeight, 0) : 0);
  const contentHeight =
    resolvedHeaderHeight +
    dataRowsHeight +
    statsRows * resolvedPinnedRowHeight +
    heightBuffer;
  const resolvedMaxHeight = Math.max(minHeight, toFiniteNumber(maxHeight, AG_GRID_DEFAULTS.maxHeight));
  const visibleHeight = Math.min(resolvedMaxHeight, Math.max(minHeight, contentHeight));

  return {
    contentHeight,
    height: visibleHeight,
    maxHeight: resolvedMaxHeight,
    shouldScroll: contentHeight > resolvedMaxHeight,
  };
};

export const calculateAgGridTableSize = ({
  columnDefs = [],
  rowData = [],
  rowCount,
  mode = TABLE_MODES.COMPSHEET,
  includePinnedRows = false,
  pinnedRowCount,
  headerHeight,
  groupHeaderHeight,
  rowHeight,
  getRowHeight,
  pinnedRowHeight,
  minWidth,
  maxWidth,
  widthBuffer,
  columnBorderReserve,
  fitMode,
  reserveScrollbar,
  scrollbarReserve,
  minHeight,
  maxHeight,
  heightBuffer,
} = {}) => {
  const resolvedRowCount = Number.isFinite(Number(rowCount))
    ? Number(rowCount)
    : Array.isArray(rowData)
      ? rowData.length
      : 0;
  const heightSizing = calculateAgGridTableHeight(resolvedRowCount, {
    mode,
    includePinnedRows,
    pinnedRowCount,
    headerHeight,
    groupHeaderHeight,
    rowHeight,
    rowData,
    getRowHeight,
    pinnedRowHeight,
    hasColumnGroups: hasAgGridColumnGroups(columnDefs),
    minHeight,
    maxHeight,
    heightBuffer,
  });

  return {
    width: calculateAgGridTableWidth(columnDefs, {
      fitMode,
      minWidth,
      maxWidth,
      widthBuffer,
      columnBorderReserve,
      reserveScrollbar: reserveScrollbar ?? heightSizing.shouldScroll,
      scrollbarReserve,
    }),
    ...heightSizing,
  };
};
